import React from 'react';
import PropTypes from 'prop-types';
import { rgba } from 'polished';

import { withStyles } from '../tools/withStyles';
import { Main } from '../components/Main';
import { Secuence } from '../components/Secuence';
import { Text } from '../components/Text';

const PDF_URL = '/melihemik.pdf';
const PDFJS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
const PDFJS_WORKER_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';

let pdfJsPromise = null;

const styles = theme => ({
  root: {},
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 14,
    marginBottom: 20,
    '@media (min-width: 600px)': {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between'
    }
  },
  title: {
    margin: 0
  },
  downloadButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid ' + rgba(theme.color.secondary.main, 0.5),
    padding: [10, 22],
    color: theme.color.secondary.light,
    fontSize: 13,
    lineHeight: 1,
    fontFamily: theme.typography.primary,
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    textDecoration: 'none',
    backgroundColor: rgba(theme.color.secondary.main, 0.08),
    boxShadow: `0 0 10px ${rgba(theme.color.secondary.main, 0.1)}`,
    cursor: 'pointer',
    transition: [
      `border-color ${theme.animation.time}ms ease-out`,
      `background-color ${theme.animation.time}ms ease-out`,
      `color ${theme.animation.time}ms ease-out`,
      `box-shadow ${theme.animation.time}ms ease-out`,
      `transform ${theme.animation.time}ms ease-out`
    ].join(','),

    '&:hover, &:focus': {
      outline: 'none',
      borderColor: theme.color.tertiary.main,
      color: theme.color.tertiary.main,
      backgroundColor: rgba(theme.color.tertiary.main, 0.15),
      boxShadow: `0 0 18px ${rgba(theme.color.tertiary.main, 0.3)}`,
      transform: 'translateY(-2px)'
    }
  },
  viewer: {
    maxWidth: 880,
    marginTop: 20
  },
  canvasHost: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 18
  },
  page: {
    position: 'relative',
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.28),
    backgroundColor: rgba(theme.color.background.dark, 0.42),
    boxShadow: `0 0 22px ${rgba(theme.color.secondary.main, 0.12)}`,
    overflow: 'hidden',
    userSelect: 'text',

    '& canvas': {
      display: 'block',
      width: '100%',
      height: 'auto',
      backgroundColor: theme.color.primary.light
    }
  },
  textLayer: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
    lineHeight: 1,
    userSelect: 'text',
    WebkitUserSelect: 'text',
    pointerEvents: 'auto',

    '& > span': {
      color: 'transparent',
      position: 'absolute',
      whiteSpace: 'pre',
      cursor: 'text',
      transformOrigin: '0% 0%',
      pointerEvents: 'all'
    },
    '& ::selection': {
      background: rgba(theme.color.secondary.main, 0.45),
      color: '#ffffff'
    },
    '& ::-moz-selection': {
      background: rgba(theme.color.secondary.main, 0.45),
      color: '#ffffff'
    }
  },
  status: {
    margin: [0, 0, 18],
    color: theme.color.secondary.main,
    fontSize: 14,
    lineHeight: 1.5,
    fontFamily: theme.typography.secondary
  },
  fallback: {
    display: 'block',
    width: '100%',
    height: 'min(76vh, 980px)',
    minHeight: 620,
    border: '1px solid ' + rgba(theme.color.secondary.dark, 0.28),
    backgroundColor: rgba(theme.color.background.dark, 0.42)
  }
});

const loadPdfJs = () => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Window is not available.'));
  }

  if (window.pdfjsLib) {
    return Promise.resolve(window.pdfjsLib);
  }

  if (!pdfJsPromise) {
    pdfJsPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = PDFJS_URL;
      script.async = true;
      script.onload = () => {
        if (window.pdfjsLib) {
          resolve(window.pdfjsLib);
        } else {
          reject(new Error('PDF renderer is not available.'));
        }
      };
      script.onerror = reject;

      document.body.appendChild(script);
    });
  }

  return pdfJsPromise;
};

class ResumeCanvas extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.object.isRequired
  };

  state = {
    loading: true,
    failed: false
  };

  componentDidMount () {
    this.renderPdf();
    window.addEventListener('resize', this.onResize);
  }

  componentWillUnmount () {
    this.renderCycle += 1;
    clearTimeout(this.resizeTimer);
    window.removeEventListener('resize', this.onResize);
  }

  renderCycle = 0;

  onResize = () => {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(this.renderPdf, 180);
  }

  clearCanvasHost () {
    if (!this.canvasHost) {
      return;
    }

    while (this.canvasHost.firstChild) {
      this.canvasHost.removeChild(this.canvasHost.firstChild);
    }
  }

  renderPdf = async () => {
    const { classes } = this.props;

    if (!this.canvasHost) {
      return;
    }

    const cycle = this.renderCycle + 1;
    this.renderCycle = cycle;
    this.setState({ loading: true, failed: false });

    try {
      const pdfjsLib = await loadPdfJs();

      if (!pdfjsLib || cycle !== this.renderCycle) {
        return;
      }

      pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;

      const pdf = await pdfjsLib.getDocument(PDF_URL).promise;
      const width = Math.max(this.canvasHost.clientWidth, 320);

      this.clearCanvasHost();

      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        if (cycle !== this.renderCycle) {
          return;
        }

        const page = await pdf.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1 });
        const scale = width / viewport.width;
        const scaledViewport = page.getViewport({ scale });
        const ratio = window.devicePixelRatio || 1;
        const pageElement = document.createElement('div');
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');

        if (!context) {
          throw new Error('Canvas context is not available.');
        }

        pageElement.className = classes.page;
        canvas.width = Math.floor(scaledViewport.width * ratio);
        canvas.height = Math.floor(scaledViewport.height * ratio);
        canvas.style.width = `${scaledViewport.width}px`;
        canvas.style.height = `${scaledViewport.height}px`;
        context.setTransform(ratio, 0, 0, ratio, 0, 0);

        await page.render({
          canvasContext: context,
          viewport: scaledViewport
        }).promise;

        pageElement.appendChild(canvas);

        const textContent = await page.getTextContent();
        const textLayerDiv = document.createElement('div');
        textLayerDiv.className = classes.textLayer;
        textLayerDiv.style.width = `${scaledViewport.width}px`;
        textLayerDiv.style.height = `${scaledViewport.height}px`;

        pageElement.appendChild(textLayerDiv);

        if (pdfjsLib.renderTextLayer) {
          pdfjsLib.renderTextLayer({
            textContent,
            container: textLayerDiv,
            viewport: scaledViewport,
            textDivs: []
          });
        }

        try {
          const annotations = await page.getAnnotations();
          if (annotations && annotations.length) {
            annotations.forEach(ann => {
              if (ann.subtype === 'Link' && ann.url) {
                const rect = scaledViewport.convertToViewportRectangle(ann.rect);
                const linkEl = document.createElement('a');
                linkEl.href = ann.url;
                linkEl.target = '_blank';
                linkEl.rel = 'noreferrer';
                linkEl.style.position = 'absolute';
                linkEl.style.left = `${Math.min(rect[0], rect[2])}px`;
                linkEl.style.top = `${Math.min(rect[1], rect[3])}px`;
                linkEl.style.width = `${Math.abs(rect[0] - rect[2])}px`;
                linkEl.style.height = `${Math.abs(rect[1] - rect[3])}px`;
                linkEl.style.cursor = 'pointer';
                linkEl.style.zIndex = 10;
                textLayerDiv.appendChild(linkEl);
              }
            });
          }
        } catch (annError) {
          // Annotation rendering is non-fatal
        }

        this.canvasHost.appendChild(pageElement);
      }

      if (cycle === this.renderCycle) {
        this.setState({ loading: false });
      }
    } catch (error) {
      this.error = error;

      if (cycle === this.renderCycle) {
        this.clearCanvasHost();
        this.setState({ loading: false, failed: true });
      }
    }
  }

  render () {
    const { classes } = this.props;
    const { loading, failed } = this.state;

    return (
      <div className={classes.viewer}>
        {loading && (
          <p className={classes.status}>
            <Text animation={{ independent: true }}>Loading Resume...</Text>
          </p>
        )}
        <div
          className={classes.canvasHost}
          ref={ref => (this.canvasHost = ref)}
          aria-label='Resume PDF'
        />
        {failed && (
          <object
            className={classes.fallback}
            data={PDF_URL}
            type='application/pdf'
            aria-label='Resume PDF'
          >
            <iframe
              className={classes.fallback}
              src={PDF_URL}
              title='Resume PDF'
            />
          </object>
        )}
      </div>
    );
  }
}

class Resume extends React.Component {
  static propTypes = {
    classes: PropTypes.object.isRequired
  };

  render () {
    const { classes } = this.props;

    return (
      <Main className={classes.root}>
        <Secuence stagger>
          <header className={classes.header}>
            <h1 className={classes.title}><Text>Resume</Text></h1>
            <a
              className={classes.downloadButton}
              href={PDF_URL}
              target='_blank'
              rel='noreferrer'
            >
              <Text>Download PDF</Text>
            </a>
          </header>
          <ResumeCanvas classes={classes} />
        </Secuence>
      </Main>
    );
  }
}

export default withStyles(styles)(Resume);
